import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Mail, MessageSquare, Send, RefreshCw, Loader2, AlertCircle, CheckCircle2,
  Settings as SettingsIcon, FileText, RotateCw, Eye, X, Search as SearchIcon,
  BarChart3, TrendingUp, TrendingDown, Clock, XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/notifications`;

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'email', label: 'Email Queue', icon: Mail },
  { id: 'sms', label: 'SMS Queue', icon: MessageSquare },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const STATUS_COLORS = {
  pending:       'bg-amber-100 text-amber-800',
  sending:       'bg-blue-100 text-blue-800',
  sent:          'bg-emerald-100 text-emerald-800',
  skipped_mock:  'bg-gray-100 text-gray-700',
  failed:        'bg-orange-100 text-orange-800',
  dead:          'bg-red-100 text-red-800',
};

const NotificationsManagement = () => {
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(null);
  const [queueEmail, setQueueEmail] = useState({ rows: [], total: 0 });
  const [queueSms, setQueueSms] = useState({ rows: [], total: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(res.data?.templates || []);
    } finally { setLoading(false); }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (_e) { /* noop */ }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, statusRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/status`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setStatus(statusRes.data);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  }, []);

  const loadEmailQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-queue`, {
        withCredentials: true,
        params: { status: filterStatus, q: search, limit: 100 },
      });
      setQueueEmail({ rows: res.data?.rows || [], total: res.data?.total || 0 });
    } finally { setLoading(false); }
  }, [filterStatus, search]);

  const loadSmsQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sms-queue`, {
        withCredentials: true,
        params: { status: filterStatus, q: search, limit: 100 },
      });
      setQueueSms({ rows: res.data?.rows || [], total: res.data?.total || 0 });
    } finally { setLoading(false); }
  }, [filterStatus, search]);

  useEffect(() => {
    if (tab === 'dashboard') loadStats();
    if (tab === 'templates') loadTemplates();
    if (tab === 'email') loadEmailQueue();
    if (tab === 'sms') loadSmsQueue();
    if (tab === 'settings') loadStatus();
  }, [tab, loadStats, loadTemplates, loadEmailQueue, loadSmsQueue, loadStatus]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="admin-notifications-page">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Notifications</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage email + SMS templates, view the delivery queue, and configure providers.
        </p>
      </header>

      {/* Tab bar */}
      <div className="flex gap-2 overflow-x-auto mb-6 pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`notif-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                active ? 'bg-[#0C1D36] text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'dashboard' && (
        <DashboardTab
          stats={stats}
          providerStatus={status}
          loading={loading}
          onReload={loadStats}
          onJumpToQueue={(filter) => {
            setFilterStatus(filter || 'all');
            setTab('email');
          }}
        />
      )}

      {tab === 'templates' && (
        <TemplatesTab
          templates={templates}
          loading={loading}
          onReload={loadTemplates}
          onEdit={setEditing}
        />
      )}

      {(tab === 'email' || tab === 'sms') && (
        <QueueTab
          tab={tab}
          rows={tab === 'email' ? queueEmail.rows : queueSms.rows}
          total={tab === 'email' ? queueEmail.total : queueSms.total}
          loading={loading}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          search={search}
          setSearch={setSearch}
          onReload={tab === 'email' ? loadEmailQueue : loadSmsQueue}
        />
      )}

      {tab === 'settings' && <SettingsTab status={status} onReload={loadStatus} />}

      {editing && (
        <EditTemplateModal
          templateKey={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadTemplates(); }}
        />
      )}
    </div>
  );
};

// ─── Dashboard tab ────────────────────────────────────────────────────────
const DashboardTab = ({ stats, providerStatus, loading, onReload, onJumpToQueue }) => {
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading delivery metrics…
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="text-center py-20 text-gray-500">
        No metrics yet.
        <button onClick={onReload} className="ml-2 underline text-[#0C1D36]">Retry</button>
      </div>
    );
  }

  const email24 = stats.email_24h || {};
  const emailAll = stats.email || {};
  const sms24 = stats.sms_24h || {};
  const wa24 = stats.whatsapp_24h || {};
  const waDelivery = stats.whatsapp_delivery_24h || {};
  const waProvider = providerStatus?.whatsapp || {};

  const kpi = (key, fallback = 0) => Number(email24[key] ?? 0);
  const totalLifetime = Object.values(emailAll).reduce((a, b) => a + Number(b || 0), 0);
  const waSent24 = Number(wa24.sent || 0);
  const waMock24 = Number(wa24.skipped_mock || 0);
  const waFailed24 = Number(wa24.failed || 0) + Number(wa24.dead || 0);
  const waPending = Number(wa24.pending || 0);

  const perEvent = stats.per_event_7d?.email || {};
  const perEventRows = Object.entries(perEvent)
    .map(([event, counts]) => {
      const sent = Number(counts.sent || 0);
      const failed = Number(counts.failed || 0) + Number(counts.dead || 0);
      const total = sent + failed + Number(counts.pending || 0) + Number(counts.skipped_mock || 0);
      const rate = total ? Math.round(((sent / total) * 1000)) / 10 : 0;
      return { event, sent, failed, total, rate };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, 12);

  const recentFail = stats.recent_failures || [];

  return (
    <div data-testid="notif-dashboard-tab">
      {/* Provider banner */}
      {providerStatus && (
        <ProviderBanner providerStatus={providerStatus} onReload={onReload} />
      )}

      {/* KPI tiles — Email 24h */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <KpiTile
          testid="kpi-sent-24h"
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Sent · 24h"
          value={kpi('sent')}
          accent="emerald"
          onClick={() => onJumpToQueue('sent')}
        />
        <KpiTile
          testid="kpi-failed-24h"
          icon={<TrendingDown className="w-4 h-4" />}
          label="Failed · 24h"
          value={kpi('failed')}
          accent="orange"
          onClick={() => onJumpToQueue('failed')}
        />
        <KpiTile
          testid="kpi-pending"
          icon={<Clock className="w-4 h-4" />}
          label="Pending"
          value={kpi('pending')}
          accent="amber"
          onClick={() => onJumpToQueue('pending')}
        />
        <KpiTile
          testid="kpi-dead"
          icon={<XCircle className="w-4 h-4" />}
          label="Dead · 24h"
          value={kpi('dead')}
          accent="red"
          onClick={() => onJumpToQueue('dead')}
        />
        <KpiTile
          testid="kpi-total-lifetime"
          icon={<Mail className="w-4 h-4" />}
          label="Lifetime total"
          value={totalLifetime.toLocaleString()}
          accent="slate"
          onClick={() => onJumpToQueue('all')}
        />
      </div>

      {/* Per-event breakdown + recent failures */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4" data-testid="per-event-breakdown">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Email — per-event breakdown (7 days)
            </h3>
            <button
              onClick={onReload}
              className="text-xs flex items-center gap-1 text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          {perEventRows.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              No email activity in the last 7 days yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-500">
                  <tr>
                    <th className="py-1.5 text-left font-medium">Event</th>
                    <th className="py-1.5 text-right font-medium">Total</th>
                    <th className="py-1.5 text-right font-medium">Sent</th>
                    <th className="py-1.5 text-right font-medium">Failed</th>
                    <th className="py-1.5 text-right font-medium">Success %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {perEventRows.map((r) => (
                    <tr key={r.event} className="hover:bg-gray-50">
                      <td className="py-1.5 font-mono text-xs text-gray-800">{r.event}</td>
                      <td className="py-1.5 text-right text-gray-700">{r.total}</td>
                      <td className="py-1.5 text-right text-emerald-700 font-medium">{r.sent}</td>
                      <td className={`py-1.5 text-right font-medium ${r.failed > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                        {r.failed}
                      </td>
                      <td className="py-1.5 text-right">
                        <span className={`text-xs font-medium ${
                          r.rate === 100 ? 'text-emerald-700' :
                          r.rate >= 90 ? 'text-emerald-700' :
                          r.rate >= 70 ? 'text-amber-700' : 'text-red-700'
                        }`}>{r.rate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="recent-failures">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-orange-500" />
            Recent failures (7d)
          </h3>
          {recentFail.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No recent failures. 🎉
            </p>
          ) : (
            <ul className="space-y-3">
              {recentFail.map((f) => (
                <li key={f.id} className="border-l-2 border-orange-300 pl-3 text-xs">
                  <div className="font-mono text-[11px] text-gray-500">{f.event_key}</div>
                  <div className="text-gray-800 truncate" title={Array.isArray(f.to) ? f.to.join(', ') : f.to}>
                    → {Array.isArray(f.to) ? f.to.join(', ') : (f.to || '—')}
                  </div>
                  <div className="text-gray-600 truncate" title={f.subject}>{f.subject}</div>
                  {f.last_error && (
                    <div className="text-orange-700 mt-0.5 truncate" title={f.last_error}>
                      {f.last_error}
                    </div>
                  )}
                  <div className="text-gray-400 text-[10px] mt-0.5">
                    attempts: {f.attempts || 0} · {f.updated_at ? new Date(f.updated_at).toLocaleString() : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {recentFail.length > 0 && (
            <button
              onClick={() => onJumpToQueue('failed')}
              className="mt-3 w-full text-xs py-2 rounded bg-gray-50 hover:bg-gray-100 text-gray-700"
              data-testid="jump-to-failed-queue"
            >
              View all failed → Email Queue
            </button>
          )}
        </div>
      </div>

      {/* SMS + WhatsApp bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="sms-summary">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> SMS · 24h <span className="text-xs text-gray-400 font-normal">(being retired)</span>
            </h3>
            <span className="text-xs text-gray-500">
              {Object.values(sms24).reduce((a, b) => a + Number(b || 0), 0)} messages
            </span>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="text-emerald-700"><b>{sms24.sent || 0}</b> sent</span>
            <span className="text-orange-700"><b>{(sms24.failed || 0) + (sms24.dead || 0)}</b> failed</span>
            <span className="text-amber-700"><b>{sms24.pending || 0}</b> pending</span>
            <span className="text-gray-500"><b>{sms24.skipped_mock || 0}</b> mock</span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="whatsapp-summary">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-emerald-600" /> WhatsApp · 24h
              {waProvider.ready ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">LIVE</span>
              ) : waProvider.mock ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">MOCK</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800">NO KEY</span>
              )}
            </h3>
            <span className="text-xs text-gray-500">
              {Object.values(wa24).reduce((a, b) => a + Number(b || 0), 0)} messages
            </span>
          </div>
          <div className="flex gap-4 text-xs mb-2">
            <span className="text-emerald-700"><b>{waSent24}</b> sent</span>
            <span className="text-orange-700"><b>{waFailed24}</b> failed</span>
            <span className="text-amber-700"><b>{waPending}</b> pending</span>
            <span className="text-gray-500"><b>{waMock24}</b> mock</span>
          </div>
          {/* Delivery breakdown (read receipts from Interakt) */}
          {Object.keys(waDelivery).length > 0 && (
            <div className="flex gap-3 text-[11px] pt-2 border-t border-gray-100">
              <span className="text-gray-600" title="Single tick (relay sent)">
                ✓ sent <b>{waDelivery.sent || 0}</b>
              </span>
              <span className="text-blue-600" title="Double tick (handset got it)">
                ✓✓ delivered <b>{waDelivery.delivered || 0}</b>
              </span>
              <span className="text-emerald-600" title="Blue tick (user opened)">
                ✓✓ read <b>{waDelivery.read || 0}</b>
              </span>
              {(waDelivery.failed || 0) > 0 && (
                <span className="text-red-600">⚠ failed <b>{waDelivery.failed}</b></span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 text-[11px] text-gray-400">
        Metrics refreshed {stats.generated_at ? new Date(stats.generated_at).toLocaleString() : ''}
      </div>
    </div>
  );
};

const ProviderBanner = ({ providerStatus, onReload }) => {
  const em = providerStatus.email || {};
  const ready = em.ready;
  const mock = em.mock;
  const tone = ready ? 'emerald' : (mock ? 'amber' : 'red');
  const msg = ready
    ? `Email is LIVE via ${em.provider?.toUpperCase?.() || 'provider'} · sender ${em.sender?.email || ''}`
    : mock
      ? 'Email is in MOCK mode — no real emails are being sent. Flip EMAIL_USE_MOCK=false in backend/.env.'
      : `Email provider (${em.provider || '?'}) has no API key/credentials configured.`;
  return (
    <div
      className={`border-l-4 rounded-r-lg px-4 py-3 mb-5 flex items-start justify-between bg-${tone}-50 border-${tone}-500 text-${tone}-900 text-sm`}
      data-testid="provider-banner"
    >
      <div className="flex items-start gap-2">
        {ready ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
        <div>{msg}</div>
      </div>
      <button onClick={onReload} className="text-xs underline">Refresh</button>
    </div>
  );
};

const KpiTile = ({ testid, icon, label, value, accent = 'slate', onClick }) => {
  const accentMap = {
    emerald: 'text-emerald-700 bg-emerald-50',
    orange:  'text-orange-700 bg-orange-50',
    amber:   'text-amber-700 bg-amber-50',
    red:     'text-red-700 bg-red-50',
    slate:   'text-slate-700 bg-slate-100',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left bg-white border border-gray-200 rounded-lg px-4 py-3 hover:shadow transition-shadow"
    >
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-medium ${accentMap[accent]}`}>
        {icon} {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
    </button>
  );
};


// ─── Templates tab ─────────────────────────────────────────────────────────
const TemplatesTab = ({ templates, loading, onReload, onEdit }) => {
  const grouped = useMemo(() => {
    const email = templates.filter((t) => t.channel === 'email');
    const sms = templates.filter((t) => t.channel === 'sms');
    return { email, sms };
  }, [templates]);

  if (loading && templates.length === 0) {
    return <div className="flex items-center justify-center py-20 text-gray-500">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading templates…
    </div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {templates.length} templates — edit any one to override the default
        </h2>
        <button
          onClick={onReload}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          data-testid="notif-templates-reload"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <Section title="Email Templates" icon={<Mail className="w-4 h-4 text-[#0C1D36]" />}>
        {grouped.email.map((t) => <TemplateCard key={t.key} tpl={t} onEdit={onEdit} />)}
      </Section>

      <Section title="SMS Templates" icon={<MessageSquare className="w-4 h-4 text-[#0C1D36]" />}>
        {grouped.sms.map((t) => <TemplateCard key={t.key} tpl={t} onEdit={onEdit} />)}
      </Section>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
      {icon} {title}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const TemplateCard = ({ tpl, onEdit }) => (
  <div
    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
    onClick={() => onEdit(tpl.key)}
    data-testid={`notif-template-${tpl.key}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-900">{tpl.label}</span>
          {tpl.diverges_from_default && (
            <span className="text-xs px-1.5 py-0.5 bg-[#F5A623]/15 text-[#8a5a00] rounded">Customised</span>
          )}
        </div>
        <div className="text-xs text-gray-500 mb-2">{tpl.description}</div>
        <div className="text-xs text-gray-400 font-mono truncate">key: {tpl.key}</div>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEdit(tpl.key); }}
        className="text-xs px-3 py-1.5 bg-[#0C1D36] text-white rounded hover:bg-[#0C1D36]/90 whitespace-nowrap"
        data-testid={`notif-template-edit-${tpl.key}`}
      >
        Edit
      </button>
    </div>
  </div>
);

// ─── Edit modal ────────────────────────────────────────────────────────────
const EditTemplateModal = ({ templateKey, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [defaultTpl, setDefaultTpl] = useState(null);
  const [preview, setPreview] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/templates/${templateKey}`, { withCredentials: true });
        setTpl(res.data.template);
        setDefaultTpl(res.data.default);
      } finally { setLoading(false); }
    })();
  }, [templateKey]);

  const onField = (k, v) => setTpl((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      const payload = {
        subject: tpl.subject, html_body: tpl.html_body, text_body: tpl.text_body,
        sms_body: tpl.sms_body, label: tpl.label,
      };
      await axios.put(`${API}/templates/${templateKey}`, payload, { withCredentials: true });
      setToast({ type: 'ok', msg: 'Template saved.' });
      setTimeout(onSaved, 400);
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Failed to save.' });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset this template to the default? Your edits will be discarded.')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/templates/${templateKey}/reset`, {}, { withCredentials: true });
      setTpl(res.data.template);
      setToast({ type: 'ok', msg: 'Reset to default.' });
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Failed to reset.' });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.trim()) { setToast({ type: 'err', msg: 'Enter a recipient.' }); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/templates/${templateKey}/test`, { to: testTo.trim() }, { withCredentials: true });
      setToast({ type: 'ok', msg: 'Test enqueued — drainer picks it up within 30s.' });
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Test failed.' });
    } finally { setTesting(false); }
  };

  if (loading || !tpl) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
      </div>
    );
  }

  const isEmail = tpl.channel === 'email';
  const variables = tpl.variables || defaultTpl?.variables || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch md:items-center justify-center p-0 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-none md:rounded-xl w-full max-w-5xl my-0 md:my-8 max-h-screen md:max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{tpl.label}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{tpl.description} · <span className="font-mono">{tpl.key}</span></p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="notif-edit-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
          {toast && (
            <div className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${
              toast.type === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
            }`}>
              {toast.type === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {toast.msg}
            </div>
          )}

          {/* Variable helper */}
          {variables.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-600 mb-2">Available variables — copy and paste into the fields below</div>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <code
                    key={v}
                    className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 font-mono cursor-pointer hover:border-[#F5A623]"
                    onClick={() => navigator.clipboard?.writeText(`{{ ${v} }}`)}
                    title="Click to copy"
                  >
                    {`{{ ${v} }}`}
                  </code>
                ))}
              </div>
            </div>
          )}

          {isEmail ? (
            <>
              <Field label="Subject">
                <input
                  type="text"
                  value={tpl.subject || ''}
                  onChange={(e) => onField('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
                  data-testid="notif-edit-subject"
                />
              </Field>
              <Field label="HTML body">
                <textarea
                  value={tpl.html_body || ''}
                  onChange={(e) => onField('html_body', e.target.value)}
                  rows={18}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
                  data-testid="notif-edit-html"
                />
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setPreview((p) => !p)}
                    className="text-xs flex items-center gap-1.5 text-[#0C1D36] hover:underline"
                  >
                    <Eye className="w-3.5 h-3.5" /> {preview ? 'Hide preview' : 'Show live preview'}
                  </button>
                  {preview && (
                    <div className="mt-2 border border-gray-300 rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-600 border-b border-gray-200">Preview</div>
                      <iframe
                        title="preview"
                        srcDoc={tpl.html_body || ''}
                        className="w-full h-96 bg-white"
                        sandbox=""
                      />
                    </div>
                  )}
                </div>
              </Field>
              <Field label="Plain-text fallback (optional but recommended)">
                <textarea
                  value={tpl.text_body || ''}
                  onChange={(e) => onField('text_body', e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
                  data-testid="notif-edit-text"
                />
              </Field>
            </>
          ) : (
            <Field label="SMS body">
              <textarea
                value={tpl.sms_body || ''}
                onChange={(e) => onField('sms_body', e.target.value)}
                rows={5}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
                data-testid="notif-edit-sms"
              />
              <div className="text-xs text-gray-500 mt-1">
                {(tpl.sms_body || '').length}/500 characters. DLT template ID env:&nbsp;
                <code className="font-mono text-gray-700">{tpl.dlt_template_id_env || '—'}</code>
              </div>
            </Field>
          )}

          {/* Test send */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-sm font-semibold text-amber-900 mb-2">Send a test {isEmail ? 'email' : 'SMS'}</div>
            <div className="flex flex-col md:flex-row gap-2">
              <input
                type={isEmail ? 'email' : 'tel'}
                placeholder={isEmail ? 'test@yourdomain.com' : '+91 98765 43210'}
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="flex-1 px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white"
                data-testid="notif-edit-test-to"
              />
              <button
                onClick={sendTest}
                disabled={testing}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-[#F5A623] text-[#0C1D36] font-semibold rounded-lg hover:bg-[#F5A623]/90 disabled:opacity-60 text-sm"
                data-testid="notif-edit-test-send"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-5 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-3 bg-gray-50">
          <button
            onClick={reset}
            disabled={saving}
            className="text-sm text-gray-600 hover:text-red-600 disabled:opacity-50"
            data-testid="notif-edit-reset"
          >
            Reset to default
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white"
              data-testid="notif-edit-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-[#0C1D36] text-white rounded-lg hover:bg-[#0C1D36]/90 disabled:opacity-60 text-sm font-semibold"
              data-testid="notif-edit-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">{label}</div>
    {children}
  </label>
);

// ─── Queue tab ─────────────────────────────────────────────────────────────
const QueueTab = ({ tab, rows, total, loading, filterStatus, setFilterStatus, search, setSearch, onReload }) => {
  const retry = async (rowId) => {
    try {
      const path = tab === 'email' ? 'email-queue' : 'sms-queue';
      await axios.post(`${API}/${path}/${rowId}/retry`, {}, { withCredentials: true });
      onReload();
    } catch (_e) { /* noop */ }
  };

  const forceDrain = async () => {
    try {
      await axios.post(`${API}/drain`, {}, { withCredentials: true });
      onReload();
    } catch (_e) { /* noop */ }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search recipient, subject, event…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onReload()}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]"
            data-testid="notif-queue-search"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          data-testid="notif-queue-status"
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="sending">Sending</option>
          <option value="sent">Sent</option>
          <option value="skipped_mock">Skipped (mock)</option>
          <option value="failed">Failed</option>
          <option value="dead">Dead</option>
        </select>
        <button
          onClick={onReload}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
        <button
          onClick={forceDrain}
          className="flex items-center gap-2 px-3 py-2 bg-[#0C1D36] text-white rounded-lg text-sm hover:bg-[#0C1D36]/90"
          data-testid="notif-queue-drain"
        >
          <Send className="w-4 h-4" /> Drain now
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Event</th>
                <th className="px-3 py-2 text-left">Recipient</th>
                <th className="px-3 py-2 text-left">{tab === 'email' ? 'Subject' : 'Message'}</th>
                <th className="px-3 py-2 text-left">Attempts</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-500">No rows match the filter.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                      {r.status || 'legacy'}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">{r.event_key || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-800 max-w-[180px] truncate" title={Array.isArray(r.to) ? r.to.join(', ') : r.to}>
                    {Array.isArray(r.to) ? r.to.join(', ') : r.to}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-[280px] truncate" title={r.subject || r.message}>
                    {r.subject || r.message || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {r.attempts || 0}{r.max_attempts ? `/${r.max_attempts}` : ''}
                    {r.last_error && <div className="text-red-600 text-[10px] truncate max-w-[120px]" title={r.last_error}>{r.last_error}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {(r.status === 'failed' || r.status === 'dead') && (
                      <button
                        onClick={() => retry(r.id)}
                        className="text-xs px-2 py-1 bg-amber-50 text-amber-900 rounded hover:bg-amber-100 inline-flex items-center gap-1"
                        data-testid={`notif-retry-${r.id}`}
                      >
                        <RotateCw className="w-3 h-3" /> Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600 border-t border-gray-200">
          {total.toLocaleString()} total
        </div>
      </div>
    </div>
  );
};

// ─── Settings tab ──────────────────────────────────────────────────────────
const SettingsTab = ({ status, onReload }) => {
  if (!status) {
    return <div className="flex items-center gap-2 text-gray-500 py-10">
      <Loader2 className="w-5 h-5 animate-spin" /> Loading…
    </div>;
  }
  const envChange = (
    <div className="text-xs text-gray-600 mt-2">
      Edit <code className="font-mono bg-gray-100 px-1 rounded">backend/.env</code> and restart the backend.
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ProviderCard title="Email" icon={<Mail className="w-5 h-5" />} status={status.email} />
      <ProviderCard title="SMS" icon={<MessageSquare className="w-5 h-5" />} status={status.sms} />

      <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">How to go live</h3>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
          <li>Obtain provider API keys (Resend / SendGrid for email, MSG91 / Twilio for SMS).</li>
          <li>Paste them into <code className="font-mono bg-gray-100 px-1 rounded">backend/.env</code> — fields already scaffolded.</li>
          <li>Set <code className="font-mono bg-gray-100 px-1 rounded">EMAIL_USE_MOCK=false</code> and/or <code className="font-mono bg-gray-100 px-1 rounded">SMS_USE_MOCK=false</code>.</li>
          <li>Restart backend: <code className="font-mono bg-gray-100 px-1 rounded">sudo supervisorctl restart backend</code>.</li>
          <li>Send a test email/SMS from the Templates tab → Edit → Send test.</li>
        </ol>
        <button
          onClick={onReload}
          className="mt-4 flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          data-testid="notif-settings-reload"
        >
          <RefreshCw className="w-4 h-4" /> Re-check provider status
        </button>
        {envChange}
      </div>
    </div>
  );
};

const ProviderCard = ({ title, icon, status }) => {
  const badge = status.ready ? (
    <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-medium">Live</span>
  ) : status.mock ? (
    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-medium">Mock mode</span>
  ) : (
    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-900 rounded font-medium">Not configured</span>
  );
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">{icon}<span className="font-semibold text-gray-900">{title}</span></div>
        {badge}
      </div>
      <dl className="text-sm space-y-1.5">
        <Row label="Provider" value={status.provider} />
        <Row label="Mock mode" value={status.mock ? 'ON' : 'OFF'} />
        <Row label="API key set" value={status.has_key ? 'Yes' : 'No'} />
        {status.sender && <Row label="From" value={`${status.sender.name} <${status.sender.email}>`} />}
      </dl>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-gray-500 text-xs">{label}</dt>
    <dd className="text-gray-900 font-mono text-xs truncate max-w-[260px] text-right" title={value}>{value}</dd>
  </div>
);

export default NotificationsManagement;
